import { DynamicConfig } from '@domain/configuration/types';
import { useGeneratedSalesPortalSupplierDynamicConfigQuery } from '@/experiences/sales-portal/blocs/documents/sales-portal-supplier-dynamic-config.generated';
import { useMemo } from 'react';

interface SupplierDynamicConfigLoaderParams {
  supplierUUID: string;
}

interface SupplierDynamicConfigLoaderResult {
  error?: Error;
  loading: boolean;
  supplierDynamicConfig: DynamicConfig | null;
}

/**
 * Loader to fetch a single supplier's dynamic config using `supplierUUID`.
 *
 * @param params - The loader parameters
 * @param params.supplierUUID - The supplier identifier
 * @returns An object containing the supplier config, loading state, and error
 *
 * @example
 * ```typescript
 * const { supplierDynamicConfig, loading } = useSupplierDynamicConfigLoader({
 *   supplierUUID: 'supplier-789',
 * });
 * ```
 */
export const useSupplierDynamicConfigLoader = ({
  supplierUUID,
}: SupplierDynamicConfigLoaderParams): SupplierDynamicConfigLoaderResult => {
  const { data, error, loading } =
    useGeneratedSalesPortalSupplierDynamicConfigQuery({
      fetchPolicy: 'cache-and-network',
      skip: !supplierUUID,
      variables: {
        supplierUUID,
      },
    });

  // Cast required because GraphQL returns dynamic_config as Record<string, unknown>
  const supplierDynamicConfig = useMemo(
    () =>
      (data?.businesses_by_pk?.supplier_metadata?.dynamic_config ??
        null) as DynamicConfig | null,
    [data?.businesses_by_pk?.supplier_metadata?.dynamic_config],
  );

  return {
    error: error as Error | undefined,
    loading,
    supplierDynamicConfig,
  };
};
